import { Route, Navigate, Routes, useNavigate, useParams, useLocation } from "react-router";
import { useEffect, useReducer, useState } from "react";
import { GetToday } from "../../../Services/Utilities";
import { format } from "date-fns";
import urlJoin from "url-join";
import { CompareFocus, CurrentDaysFocus } from "../../../Services/DataService/Model/DailyEntry";
import { IHeaderSetters } from "../Journal";
import { Nullable } from "primereact/ts-helpers";
import { CompletableTextGroup, LoadingBlock, PopupQuote, TextEntryList } from "../SharedComponents";
import { DailyEntryViewModel, SimpleTextViewModel } from "../../../ViewModel";
import { GetCorrelation } from "../../../ViewModel/Utilities";
import { Button } from "primereact/button";
import React from "react";
import { ItemsManager } from "../../../ViewModel/Services/ItemsManager";
import { TextAreaSection } from "../SharedComponents/ModifiableText/TextAreaSection.tsx/TextAreaSection";
import { DailyScore } from "./DailyScore/DailyScore";
import { DataEntityBase } from "../../../Services/DataService/Model";
import { JournalStrings } from "../SharedComponents/JournalStrings";
import { appInsights } from "../../../Services/AppInsightsService";
// import background from '../../../assets/background.png';

export const DefaultDayRoute = "morning";


export function DailyEntryPage({ headerSetters }: { headerSetters: IHeaderSetters }) {
    return (
        <Routes>
            <Route path="/:dailyId?/*" element={<DailyEntry headerSetters={headerSetters} />} />
        </Routes>
    )
}

function DailyEntry({ headerSetters }: { headerSetters: IHeaderSetters }) {
    //
    // Define all the state variables here
    //
    const [activeEntry, setActiveEntry] = useState<DailyEntryViewModel | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [, forceUpdate] = useReducer(x => x + 1, 0);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { dailyId } = useParams();

    //
    // Need to async fetch data
    //
    useEffect(() => {
        var tempEntryViewModel: Nullable<DailyEntryViewModel> = undefined;
        const callback = () => {
            forceUpdate();
            headerSetters.setHeaderTitle("Your Daily Entry");
            headerSetters.setHeaderDate(format(tempEntryViewModel!.TypedData.date, 'M-d-yyyy'));
            headerSetters.setIsReadyToRender(true);
        };

        async function fetchData() {

            headerSetters.setIsReadyToRender(false);
            if (dailyId === undefined) {
                navigate(urlJoin(".", GetToday().toISOString()));
                return;
            }


            try {
                activeEntry?.PropertyChanged.Unsubscribe(callback);
                tempEntryViewModel = await DailyEntryViewModel.Manager.GetItemByKey(GetCorrelation(), dailyId)
                if (!tempEntryViewModel) {
                    tempEntryViewModel = await DailyEntryViewModel.Manager.CreateItem(GetCorrelation(), DataEntityBase.OptionsFromId(dailyId));
                    tempEntryViewModel.TypedData.date = new Date(dailyId);
                }
                setActiveEntry(tempEntryViewModel);
                tempEntryViewModel.PropertyChanged.Subscribe(callback);
                tempEntryViewModel.HydratePromise.then(() => callback())

                if (pathname.endsWith(dailyId)) {
                    var targetRoute = urlJoin(".", tempEntryViewModel.TypedData.viewCurentFocus);
                    navigate(targetRoute);
                }
            }
            catch (error: any) {
                setErrorMessage(error);
            };
        }

        fetchData();

        return () => {
            activeEntry?.PropertyChanged.Unsubscribe(callback);
        }
    }, [dailyId]);


    //
    // If we are still loading, just show the loading block
    //
    if (!activeEntry || !activeEntry.isHydrated) {
        appInsights.trackTrace({ message: 'DailyEntryPageNotReady', properties: { reason: 'activeEntry undefined or not hydrated', pathname: pathname } });
        return <LoadingBlock showLogo />;
    }





    return (
        <div className='dailyEntryPage h-full v-full'>
            <Routes>
                <Route path={urlJoin(CurrentDaysFocus.Morning, '/*')} element={<MorningFocus activeEntry={activeEntry} setHeaders={headerSetters} />} />
                <Route path={urlJoin(CurrentDaysFocus.Execution, '/*')} element={<DailyExecution activeEntry={activeEntry} setHeaders={headerSetters} />} />
                <Route path={urlJoin(CurrentDaysFocus.Review1, '/*')} element={<DailyReview1 activeEntry={activeEntry} setHeaders={headerSetters} />} />
                <Route path={urlJoin(CurrentDaysFocus.Review2, '/*')} element={<DailyReview2 activeEntry={activeEntry} setHeaders={headerSetters} />} />

                <Route path="*" element={<Navigate to={urlJoin(".", CurrentDaysFocus.Morning)} />} />
            </Routes >
            {errorMessage !== undefined && <p style={{ color: "red" }}>{errorMessage}</p>} {/* Display error message */}

        </div>

    );
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// #region MorningFocus
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function MorningFocus({ activeEntry, setHeaders }: { activeEntry: DailyEntryViewModel, setHeaders: IHeaderSetters }) {
    const [preventContinue, setPreventContinue] = React.useState<boolean>(activeEntry.IsMorningContinueDisabled);
    const navigate = useNavigate();


    useEffect(() => {
        const updateCanContinue = () => {
            setPreventContinue(activeEntry.IsMorningContinueDisabled);
        }
        activeEntry.PropertyChanged.Subscribe(updateCanContinue);
        return () => {
            activeEntry.PropertyChanged.Unsubscribe(updateCanContinue);
        }
    }, [activeEntry]);

    useEffect(() => {
        setHeaders.setSubtitle('Craft a great day');
    }, [setHeaders]);

    const showNext = CompareFocus(activeEntry.TypedData.viewCurentFocus, activeEntry.TypedData.status) > 0;

    return (
        <>
            <div>
                <div className='h-full w-full flex flex-column'>

                    <TextEntryList
                        title={JournalStrings.TutorialFlyout["gl-daily-grateful"].Title}
                        entries={activeEntry.gratefulItems}
                        className="gl-daily-grateful"
                        headerClassName="gl-today-grateful-header"
                        tutorialSteps={[
                            {
                                target: '.gl-daily-grateful',
                                content: (
                                    <>
                                        <>
                                            <p><strong>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-0-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-0-1"]}</p>
                                            <p><strong>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-1-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-1-1"]}</p>

                                            <p><strong>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-2-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-2-1"]}</p>

                                            <p><strong>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-3"]}</strong></p>
                                            <ul>
                                                <li>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-4"]}</li>
                                                <li>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-5"]}</li>
                                                <li>{JournalStrings.TutorialFlyout["gl-daily-grateful"]["Content-6"]}</li>
                                            </ul>
                                        </>

                                    </>
                                ),
                                disableBeacon: true,
                                offset: 0
                            }
                        ]}
                    />

                    {/* <TextEntryList
                        title='What would make today great?'
                        entries={activeEntry.greatItems as unknown as ItemsManager<SimpleTextViewModel>}
                        onChanged={updateCanContinue}
                    /> */}
                    <TextEntryList
                        title={JournalStrings.TutorialFlyout["gl-daily-outcomes"].Title}
                        entries={activeEntry.outcomesItems as unknown as ItemsManager<SimpleTextViewModel>}
                        className="gl-daily-outcomes"
                        tutorialSteps={[
                            {
                                target: '.gl-daily-outcomes',
                                content: (
                                    <>
                                        <p><strong>{JournalStrings.TutorialFlyout["gl-daily-outcomes"]["Content-0-0"]}</strong><strong>{JournalStrings.TutorialFlyout["gl-daily-outcomes"]["Content-0-2"]}</strong></p>

                                        <p><strong>{JournalStrings.TutorialFlyout["gl-daily-outcomes"]["Content-1-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-outcomes"]["Content-1-1"]}</p>

                                        <p><strong>{JournalStrings.TutorialFlyout["gl-daily-outcomes"]["Content-2"]}</strong></p>
                                        <ul>
                                            <li>{JournalStrings.TutorialFlyout["gl-daily-outcomes"]["Content-3"]}</li>
                                            <li>{JournalStrings.TutorialFlyout["gl-daily-outcomes"]["Content-4"]}</li>
                                            <li>{JournalStrings.TutorialFlyout["gl-daily-outcomes"]["Content-5"]}</li>
                                        </ul>
                                    </>
                                ),
                                disableBeacon: true,
                                offset: 0
                            }

                        ]}
                    />
                    <TextEntryList
                        title={JournalStrings.TutorialFlyout["gl-daily-tasks"].Title}
                        entries={activeEntry.mostImportantTasks as unknown as ItemsManager<SimpleTextViewModel>}
                        className="gl-daily-tasks"
                        tutorialSteps={[
                            {
                                target: '.gl-daily-tasks',
                                content: (
                                    <>
                                        <p><strong>{JournalStrings.TutorialFlyout["gl-daily-tasks"]["Content-0-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-tasks"]["Content-0-1"]}<strong>{JournalStrings.TutorialFlyout["gl-daily-tasks"]["Content-0-2"]}</strong></p>

                                        <p><strong>{JournalStrings.TutorialFlyout["gl-daily-tasks"]["Content-1-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-tasks"]["Content-1-1"]}</p>

                                        <p><strong>{JournalStrings.TutorialFlyout["gl-daily-tasks"]["Content-2"]}</strong></p>
                                        <ul>
                                            <li>{JournalStrings.TutorialFlyout["gl-daily-tasks"]["Content-3"]}</li>
                                            <li>{JournalStrings.TutorialFlyout["gl-daily-tasks"]["Content-4"]}</li>
                                            <li>{JournalStrings.TutorialFlyout["gl-daily-tasks"]["Content-5"]}</li>
                                        </ul>
                                    </>
                                ),
                                disableBeacon: true,
                                offset: 0
                            }

                        ]}
                    />
                    <Button
                        className='gl-continue-button gl-button-standard-typography'
                        disabled={preventContinue}
                        label={showNext ? "Next" : "Finish"}
                        onClick={() => {
                            activeEntry.TypedData.viewCurentFocus = CurrentDaysFocus.Execution;
                            const comparisone = CompareFocus(activeEntry.TypedData.viewCurentFocus, activeEntry.TypedData.status) !== 0;
                            activeEntry.TypedData.status = comparisone ? activeEntry.TypedData.status : CurrentDaysFocus.Execution;
                            showNext ? navigate(activeEntry.Route) : navigate('/journal/home');
                        }}
                    />
                </div>
            </div>

            <PopupQuote dailyEntry={activeEntry} />
        </>
    );
}




////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// #endregion MorningFocus
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////





////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// #region DailyExecution
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function DailyExecution({ activeEntry, setHeaders }: { activeEntry: DailyEntryViewModel, setHeaders: IHeaderSetters }) {

    const navigate = useNavigate();

    useEffect(() => {
        setHeaders.setSubtitle('Make the day great');
    }, [setHeaders]);


    const showNext = CompareFocus(activeEntry.TypedData.viewCurentFocus, activeEntry.TypedData.status) > 0;

    return (

        <div className='h-full w-full flex flex-column justify-content-between'>
            <div className='pt-1 pb-1'>
                {/* <CompletableTextGroup
                    title='Make today great by...'
                    entries={activeEntry.greatItems}
                /> */}

                <CompletableTextGroup
                    title={JournalStrings.TutorialFlyout["gl-daily-outcomes-checklist"].Title}
                    entries={activeEntry.outcomesItems}
                    className="gl-daily-outcomes-checklist"
                    tutorialSteps={[
                        {
                            target: '.gl-daily-outcomes-checklist',
                            content: (
                                <>
                                    <p><strong>{JournalStrings.TutorialFlyout["gl-daily-outcomes-checklist"]["Content-0-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-outcomes-checklist"]["Content-0-1"]}</p>
                                    <p>{JournalStrings.TutorialFlyout["gl-daily-outcomes-checklist"]["Content-1"]}</p>

                                    <p><strong>{JournalStrings.TutorialFlyout["gl-daily-outcomes-checklist"]["Content-2-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-outcomes-checklist"]["Content-2-1"]}</p>
                                </>
                            ),
                            disableBeacon: true,
                            offset: 0
                        }
                    ]}
                />
                <CompletableTextGroup
                    title={JournalStrings.TutorialFlyout["gl-daily-tasks-checklist"].Title}
                    entries={activeEntry.mostImportantTasks}
                    className="gl-daily-tasks-checklist"
                    tutorialSteps={[
                        {
                            target: '.gl-daily-tasks-checklist',
                            content: (
                                <>
                                    <p><strong>{JournalStrings.TutorialFlyout["gl-daily-tasks-checklist"]["Content-0-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-tasks-checklist"]["Content-0-1"]}</p>
                                    <p><strong>{JournalStrings.TutorialFlyout["gl-daily-tasks-checklist"]["Content-1-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-tasks-checklist"]["Content-1-1"]}</p>
                                    <p>{JournalStrings.TutorialFlyout["gl-daily-tasks-checklist"]["Content-2-0"]}<strong>{JournalStrings.TutorialFlyout["gl-daily-tasks-checklist"]["Content-2-1"]}</strong></p>
                                </>
                            ),
                            disableBeacon: true,
                            offset: 0
                        }

                    ]}
                />

            </div>

            <div className='flex flex-row justify-content-around'>

                <Button
                    className='gl-continue-button gl-button-standard-typography'
                    label="Previous"
                    onClick={() => {
                        activeEntry.TypedData.viewCurentFocus = CurrentDaysFocus.Morning;
                        navigate(activeEntry.Route);
                    }}
                />

                <Button
                    className='gl-continue-button gl-button-standard-typography'
                    label={showNext ? "Next" : "Finish"}
                    onClick={() => {
                        activeEntry.TypedData.viewCurentFocus = CurrentDaysFocus.Review1;
                        activeEntry.TypedData.status = CurrentDaysFocus.Review1;
                        showNext ? navigate(activeEntry.Route) : navigate('/journal/home');
                    }}
                />
            </div>
        </div>
    );
}




////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// #endregion DailyExecution
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////





////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// #region DailyReview1
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function DailyReview1({ activeEntry, setHeaders }: { activeEntry: DailyEntryViewModel, setHeaders: IHeaderSetters }) {

    const navigate = useNavigate();
    const [preventContinue, setPreventContinue] = React.useState<boolean>(activeEntry.IsMorningContinueDisabled);

    useEffect(() => {
        const updateCanContinue = () => {
            setPreventContinue(!activeEntry.isHighlightsWritten);
        }
        activeEntry.PropertyChanged.Subscribe(updateCanContinue);
        return () => {
            activeEntry.PropertyChanged.Unsubscribe(updateCanContinue);
        }
    }, [activeEntry]);

    useEffect(() => {
        setHeaders.setSubtitle('Reflect on your day');
    }, [setHeaders]);

    return (

        <div className='h-full w-full flex flex-column justify-content-between'>

            <div className="flex flex-column justify-content-center flex-grow-1">

                <DailyScore

                    onScoreChange={(score) => activeEntry.TypedData.score = score * 2}
                    score={activeEntry.TypedData.score / 2}
                    className="gl-daily-score"
                    tutorialSteps={[
                        {
                            target: '.gl-daily-score',
                            title: JournalStrings.TutorialFlyout["gl-daily-score"].Title,
                            content: (
                                <>
                                    <p>{JournalStrings.TutorialFlyout["gl-daily-score"]["Content-0-0"]}<strong>{JournalStrings.TutorialFlyout["gl-daily-score"]["Content-0-1"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-score"]["Content-0-2"]}<strong>{JournalStrings.TutorialFlyout["gl-daily-score"]["Content-0-3"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-score"]["Content-0-4"]}</p>
                                    <p><strong>{JournalStrings.TutorialFlyout["gl-daily-score"]["Content-1-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-score"]["Content-1-1"]}</p>
                                </>
                            ),
                            disableBeacon: true,
                            offset: 0
                        }
                    ]}

                />
                <TextEntryList
                    title={JournalStrings.TutorialFlyout["gl-daily-magic-moments"].Title}
                    entries={activeEntry.magicMomentsItems as any as ItemsManager<SimpleTextViewModel>}
                    className="gl-daily-magic-moments"
                    tutorialSteps={[
                        {
                            target: '.gl-daily-magic-moments',
                            content: (
                                <>
                                    <p><strong>{JournalStrings.TutorialFlyout["gl-daily-magic-moments"]["Content-0-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-magic-moments"]["Content-0-1"]}</p>
                                    <p>{JournalStrings.TutorialFlyout["gl-daily-magic-moments"]["Content-1-0"]}<strong>{JournalStrings.TutorialFlyout["gl-daily-magic-moments"]["Content-1-1"]}</strong></p>
                                </>
                            ),
                            disableBeacon: true,
                            offset: 0
                        }

                    ]}
                />

                <TextEntryList
                    title={JournalStrings.TutorialFlyout["gl-daily-accomplishments"].Title}
                    entries={activeEntry.achievementsItems as any as ItemsManager<SimpleTextViewModel>}
                    className="gl-daily-accomplishments"
                    tutorialSteps={[
                        {
                            target: '.gl-daily-accomplishments',
                            content: (
                                <>
                                    <p><strong>{JournalStrings.TutorialFlyout["gl-daily-accomplishments"]["Content-0-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-accomplishments"]["Content-0-1"]}</p>
                                    <p>{JournalStrings.TutorialFlyout["gl-daily-accomplishments"]["Content-1-0"]}<strong>{JournalStrings.TutorialFlyout["gl-daily-accomplishments"]["Content-1-1"]}</strong></p>
                                </>
                            ),
                            disableBeacon: true,
                            offset: 0
                        }
                    ]}
                />
            </div>


            <div className='flex flex-row justify-content-around flex-none'>
                <Button
                    className='gl-continue-button gl-button-standard-typography'
                    label="Previous"
                    onClick={() => {
                        activeEntry.TypedData.viewCurentFocus = CurrentDaysFocus.Execution;
                        navigate(activeEntry.Route);
                    }}
                />

                <Button
                    className='gl-continue-button gl-button-standard-typography'
                    label="Next"
                    disabled={preventContinue}
                    onClick={() => {
                        activeEntry.TypedData.viewCurentFocus = CurrentDaysFocus.Review2;

                        const comparisone = CompareFocus(activeEntry.TypedData.viewCurentFocus, activeEntry.TypedData.status);
                        activeEntry.TypedData.status = comparisone ? activeEntry.TypedData.status : CurrentDaysFocus.Review2;

                        navigate(activeEntry.Route);
                    }}
                />
            </div>
        </div>
    );
}



////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// #endregion DailyReview1
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////




////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// #region DailyReview2
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function DailyReview2({ activeEntry, setHeaders }: { activeEntry: DailyEntryViewModel, setHeaders: IHeaderSetters }) {


    const [reviewText, setReviewText] = useState<string>(activeEntry.TypedData.daysEvents);
    const navigate = useNavigate();
    const [preventContinue, setPreventContinue] = React.useState<boolean>(activeEntry.IsMorningContinueDisabled);

    useEffect(() => {
        const updateCanContinue = () => {
            setPreventContinue(!activeEntry.isCompletable);
        }
        activeEntry.PropertyChanged.Subscribe(updateCanContinue);
        return () => {
            activeEntry.PropertyChanged.Unsubscribe(updateCanContinue);
        }
    }, [activeEntry]);

    useEffect(() => {
        setHeaders.setSubtitle('Reflect on your day');
    }, [setHeaders]);

    return (

        <div className='h-full w-full flex flex-column justify-content-between'>


            <div className="flex-auto flex flex-column justify-content-center">




                <TextEntryList
                    title={JournalStrings.TutorialFlyout["gl-daily-better-day"].Title}
                    entries={activeEntry.evenBetterIfItems}
                    className="gl-daily-better-day"
                    tutorialSteps={[
                        {
                            target: '.gl-daily-better-day',
                            content: (
                                <>
                                    <p>{JournalStrings.TutorialFlyout["gl-daily-better-day"]["Content-0-0"]}<strong>{JournalStrings.TutorialFlyout["gl-daily-better-day"]["Content-0-1"]}</strong></p>
                                    <p>{JournalStrings.TutorialFlyout["gl-daily-better-day"]["Content-1-0"]}<strong>{JournalStrings.TutorialFlyout["gl-daily-better-day"]["Content-1-1"]}</strong></p>
                                </>
                            ),
                            disableBeacon: true,
                            offset: 0
                        }
                    ]}
                />
                <TextAreaSection
                    title={JournalStrings.TutorialFlyout["gl-daily-key-events"].Title}
                    text={reviewText}
                    onChanged={(e) => {
                        setReviewText(e.target.value);
                        activeEntry.TypedData.daysEvents = e.target.value;
                    }}
                    className="gl-daily-key-events"
                    tutorialSteps={[
                        {
                            target: '.gl-daily-key-events',
                            title: "Description of Today's Key Events",
                            content: (
                                <>
                                    <p><strong>{JournalStrings.TutorialFlyout["gl-daily-key-events"]["Content-0-0"]}</strong>{JournalStrings.TutorialFlyout["gl-daily-key-events"]["Content-0-1"]}</p>
                                    <p>{JournalStrings.TutorialFlyout["gl-daily-key-events"]["Content-1-0"]}</p>
                                    <p><strong>{JournalStrings.TutorialFlyout["gl-daily-key-events"]["Content-2"]}</strong></p>
                                </>
                            ),
                            disableBeacon: true,
                            offset: 0
                        }

                    ]}
                />
            </div>


            <div className='flex flex-row justify-content-around'>
                <Button
                    className='gl-continue-button'
                    label="Previous"
                    onClick={() => {
                        activeEntry.TypedData.viewCurentFocus = CurrentDaysFocus.Review1;
                        navigate(activeEntry.Route);
                    }}
                />

                <Button
                    className='gl-continue-button'
                    label="Complete"
                    disabled={preventContinue}
                    onClick={() => {
                        activeEntry.TypedData.viewCurentFocus = CurrentDaysFocus.Complete;
                        activeEntry.TypedData.status = CurrentDaysFocus.Complete;
                        navigate('/journal/home');
                    }}
                />
            </div>
        </div>
    );
}



////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// #endregion DailyReview2
//
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////